/**
 * Match-specific mini standings table (matches design): #, Team (kit + name), PL W D L GF GA
 * GD PTS, with the two teams in this match highlighted. Kept separate from the league page's
 * `LeagueStandingsTable` so restyling here can't affect that screen.
 */

import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { SoccerKit } from '../../ui/SoccerKit';
import { getTeamKitConfigFromTeam } from '../../../helpers/teamKit.helper';
import { useTheme } from '../../../src/theme/useTheme';
import type { StandingRow } from '../../../src/leagueDetail';
import { getMatchHubStandingsTableStyles } from './MatchHubStandingsTable.styles';

/** Cap OS font scaling on this dense grid so large display settings can't break the columns. */
const DENSE_MAX_FONT_SCALE = 1.2;
const STAT_COLUMNS = ['PL', 'W', 'D', 'L', 'GF', 'GA', 'GD'] as const;

export interface MatchHubStandingsTableProps {
  rows: StandingRow[];
  highlightTeamIds: ReadonlySet<string>;
}

function gd(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

export const MatchHubStandingsTable: React.FC<MatchHubStandingsTableProps> = ({
  rows,
  highlightTeamIds,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubStandingsTableStyles(theme), [theme]);

  return (
    <View style={styles.card} testID="match-hub-standings-table">
      <View style={styles.headRow}>
        <Text style={styles.headPos}>#</Text>
        <Text style={styles.headTeam}>Team</Text>
        {STAT_COLUMNS.map((c) => (
          <Text key={c} style={styles.headCell}>
            {c}
          </Text>
        ))}
        <Text style={styles.headCell}>PTS</Text>
      </View>

      {rows.map((row) => {
        const kit = getTeamKitConfigFromTeam({
          name: row.displayName,
          kitPrimary: row.kitPrimary ?? null,
          kitSecondary: row.kitSecondary ?? null,
          kitType: row.kitType ?? null,
        });
        const highlighted = highlightTeamIds.has(row.teamId);
        return (
          <View key={row.teamId} style={[styles.row, highlighted && styles.rowHighlighted]}>
            <Text style={styles.pos}>{row.rank}</Text>
            <View style={styles.team}>
              <SoccerKit primary={kit.primary} secondary={kit.secondary} type={kit.type} size={18} />
              <Text style={styles.teamName} numberOfLines={1} maxFontSizeMultiplier={DENSE_MAX_FONT_SCALE}>
                {row.displayName}
              </Text>
            </View>
            <Text style={styles.statCell} maxFontSizeMultiplier={DENSE_MAX_FONT_SCALE}>{row.played}</Text>
            <Text style={styles.statCell} maxFontSizeMultiplier={DENSE_MAX_FONT_SCALE}>{row.won}</Text>
            <Text style={styles.statCell} maxFontSizeMultiplier={DENSE_MAX_FONT_SCALE}>{row.drawn}</Text>
            <Text style={styles.statCell} maxFontSizeMultiplier={DENSE_MAX_FONT_SCALE}>{row.lost}</Text>
            <Text style={styles.statCell} maxFontSizeMultiplier={DENSE_MAX_FONT_SCALE}>{row.goalsFor}</Text>
            <Text style={styles.statCell} maxFontSizeMultiplier={DENSE_MAX_FONT_SCALE}>{row.goalsAgainst}</Text>
            <Text style={styles.statCell} maxFontSizeMultiplier={DENSE_MAX_FONT_SCALE}>{gd(row.goalDiff)}</Text>
            <Text style={styles.ptsCell} maxFontSizeMultiplier={DENSE_MAX_FONT_SCALE}>{row.points}</Text>
          </View>
        );
      })}
    </View>
  );
};
